'use client';

import * as React from 'react';

// Utils
import { ListingReview } from '@gql';
import { capitalizeAllWords } from '@utils/string-helpers';

// UI
import { Typography } from '@components/typography';
import { ReviewStars } from './review-stars';
import { ExpandableText } from '@components/expandable-text';
import { useReviews } from './reviews-context';

type Props = {
  review: Pick<ListingReview, 'date' | 'authorName' | 'body' | 'rating'>;
  showBorder?: boolean;
};

// Helper function to get initials from name
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Helper function to get time ago string
const getTimeAgo = (date: string): string => {
  const now = new Date();
  const reviewDate = new Date(date);
  const diffInWeeks = Math.floor(
    (now.getTime() - reviewDate.getTime()) / (1000 * 60 * 60 * 24 * 7)
  );

  // If older than 4 weeks (approximately 1 month), show month and year
  if (diffInWeeks >= 4) {
    return reviewDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  }

  if (diffInWeeks === 0) return 'This week';
  if (diffInWeeks === 1) return '1 week ago';
  return `${diffInWeeks} weeks ago`;
};

export const IndividualReview: React.FC<Props> = ({
  review,
  showBorder = false,
}) => {
  const { searchValue, reviewsLoading } = useReviews();
  return (
    <div
      className={`space-y-2 ${showBorder ? 'border-b border-gray-light pb-6' : ''}`}
    >
      {/* Avatar and Name/Stars Row */}
      <div className="flex items-center space-x-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-primary-xlight rounded-full flex items-center justify-center">
            <Typography
              variant="body2"
              component="span"
              className="text-primary-main font-medium text-lg"
            >
              {review.authorName ? getInitials(review.authorName) : 'GU'}
            </Typography>
          </div>
        </div>

        {/* Name and Stars */}
        <div className="flex flex-col items-center">
          <div className="flex flex-row items-center w-full">
            <Typography
              variant="body1"
              component="span"
              className="font-medium text-primary-main text-left w-full flex"
            >
              {review.authorName
                ? capitalizeAllWords(review.authorName)
                : 'Guest User'}
            </Typography>
          </div>

          <div className="flex flex-row items-center space-x-2 h-6 justify-center">
            <ReviewStars rating={review.rating || 5} />
            {review.date && (
              <>
                <div className="text-gray-main">&bull;</div>
                <Typography
                  variant="body2"
                  component="span"
                  className="text-gray-main"
                >
                  {review.date ? getTimeAgo(review.date) : 'Recently'}
                </Typography>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Review Text - aligned with avatar */}
      <div className="flex items-start">
        <div className="flex-1">
          {review.body && (
            <ExpandableText
              text={review.body}
              maxLines={3}
              maxLength={200}
              typographyVariant="body2"
              className="text-primary-main items-start"
              buttonColor="primary"
              buttonVariant="text"
              buttonContainerClassName="-ml-4 justify-start pt-1"
              buttonClassName="text-primary-main underline text-sm font-medium"
              highlightQuery={reviewsLoading ? '' : searchValue}
            />
          )}
        </div>
      </div>
    </div>
  );
};
